<template>
    <div class="configure-from-mechanic-form">
        <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="configure-from-mechanic-form__notice"
        >
            {{
                $t(
                    modelValue.source === 'system'
                        ? 'admin.offerPresets.wizard.mechanicFormTagNoticeSystem'
                        : 'admin.offerPresets.wizard.mechanicFormTagNotice'
                )
            }}
        </v-alert>
        <div class="configure-from-mechanic-form__editor">
            <div class="configure-from-mechanic-form__group-controls">
                <span class="configure-from-mechanic-form__group-count">
                    {{
                        $t('admin.offerPresets.wizard.offerGroupsCount', {
                            count: productOfferGroupCount,
                        })
                    }}
                </span>
                <v-btn
                    v-if="canRemoveOfferGroup"
                    color="primary"
                    variant="outlined"
                    size="small"
                    prepend-icon="mdi-minus"
                    data-test="remove-offer-group"
                    @click="removeLastProductOfferGroup()"
                >
                    {{ $t('admin.offerPresets.wizard.removeOfferGroup') }}
                </v-btn>
                <v-btn
                    v-else
                    color="primary"
                    variant="outlined"
                    size="small"
                    prepend-icon="mdi-plus"
                    data-test="add-offer-group"
                    @click="addProductOfferGroups()"
                >
                    {{ $t('admin.offerPresets.wizard.addOfferGroup') }}
                </v-btn>
            </div>

            <div class="configure-from-mechanic-form__tag-layouts">
                <span class="configure-from-mechanic-form__tag-layouts-label">
                    {{ toSentenceCase($t('admin.offerPresets.wizard.attachedPriceTag')) }}
                </span>
                <span class="configure-from-mechanic-form__tag-layouts-hint">
                    {{ $t('admin.offerPresets.wizard.attachedPriceTagHint') }}
                </span>
                <price-tag-gallery
                    carousel
                    :items="galleryItems"
                    :selected-id="modelValue.priceTagId"
                    :disabled="isTagLocked"
                    :context-source="contextSource"
                    @select="selectPriceTag"
                />
            </div>

            <offer-mechanic
                v-if="isReady"
                vuex-module="promotions"
                :namespace="namespace"
                :authoring-mode="true"
                :show-price-tag-gallery="false"
                @create-pog="addProductOfferGroups"
                @delete-pog="deleteProductOfferGroup"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { cloneDeep } from 'lodash';
import offerMechanicTemplates from '@enums/offer-mechanic-templates';
import { toSentenceCase } from '@/js/utils/string-utils';
import { getPriceTagLabel } from '@/js/utils/offer-template-label';

const props = defineProps({
    modelValue: { type: Object, required: true },
    priceTags: { type: Array, default: () => [] },
    contextSource: { type: Object, required: true },
});
const emit = defineEmits(['update:modelValue']);
const store = useStore();
const namespace = '__offer-preset-draft__';
const MIN_OFFER_GROUPS = 1;
const MAX_OFFER_GROUPS = 2;
const getDraft = () => store.state.promotions.stagingArea[namespace];

const isReady = computed(() => Boolean(getDraft()?.offerMechanic));
const productOfferGroupCount = computed(() => getDraft()?.productOfferGroups?.length || 0);
const canRemoveOfferGroup = computed(() => productOfferGroupCount.value >= MAX_OFFER_GROUPS);
const isTagLocked = computed(
    () => props.modelValue.source === 'system' && !!props.modelValue.priceTagId
);
const galleryItems = computed(() => {
    if (!props.modelValue.templateStructure) return [];

    const tags = isTagLocked.value
        ? props.priceTags.filter(tag => tag.id === props.modelValue.priceTagId)
        : props.priceTags;

    return tags.map(tag => ({
        id: tag.id,
        label: getPriceTagLabel({
            id: tag.id,
            name: tag.name,
            currencySymbol: props.contextSource.currencySymbol,
        }),
        priceTag: tag,
        offerMechanic: props.modelValue.templateStructure,
        displayValues: props.modelValue.displayValues || {},
    }));
});

const createEmptyMechanic = () => ({
    description: null,
    maximumPerPurchase: null,
    maximumPerCustomer: null,
    customerAvailability: store.state.clientConfig.offerDefaults?.customerAvailability ?? null,
    promotionAvailability: null,
    couponBarcode: null,
    isSingleTripPromo: null,
    tiers: [
        {
            globalRewards: [],
            globalRequirements: [],
            productOfferGroups: [],
            freeGifts: [],
            uiFields: { isIndividualGroups: null },
        },
    ],
    uiFields: { offerTemplate: null, templateDescription: null },
});

const getStartingMechanic = () => {
    if (props.modelValue.templateStructure) return props.modelValue.templateStructure;
    const presets = store.state.offerMechanicPresets.offerMechanicPresets || [];
    const defaultPreset = presets.find(
        preset => preset.offerTemplate === offerMechanicTemplates.newPricePerItem
    );
    return defaultPreset?.templateStructure || createEmptyMechanic();
};

const getDefaultDisplayValues = tag =>
    (tag?.fields || []).reduce((values, field) => {
        const match = /^displayValues\.(\w+)$/.exec(field.binding);
        if (match) values[match[1]] = field.defaultValue;
        return values;
    }, {});

const selectPriceTag = priceTagId => {
    if (!priceTagId || priceTagId === props.modelValue.priceTagId) return;
    if (isTagLocked.value && priceTagId !== props.modelValue.priceTagId) return;

    const tag = props.priceTags.find(item => item.id === priceTagId);
    emit('update:modelValue', {
        ...props.modelValue,
        priceTagId,
        displayValues: getDefaultDisplayValues(tag),
    });
};

// Synchronous so the namespace exists before offer-mechanic first renders.
const initialise = () => {
    const mechanic = cloneDeep(getStartingMechanic());
    mechanic.tiers = mechanic.tiers?.length ? mechanic.tiers : createEmptyMechanic().tiers;
    const groupCount = Math.max(
        1,
        ...(mechanic.tiers || []).map(tier => (tier.productOfferGroups || []).length)
    );
    const groups = Array.from({ length: groupCount }, (_, index) => ({
        _id: index + 1,
        description: `Offer group ${index + 1}`,
        products: [],
    }));
    // Blueprints store groups without ids; give them sandbox ids but keep their conditions.
    mechanic.tiers.forEach(tier => {
        tier.productOfferGroups = groups.map((group, index) => ({
            isOptionalOfferGroup: false,
            requirements: [],
            rewards: [],
            ...(tier.productOfferGroups || [])[index],
            _id: group._id,
            description: group.description,
        }));
    });
    store.commit('promotions/resetStagingArea', namespace);
    store.commit('promotions/setStagingAreaFields', {
        namespace,
        fieldsToUpdate: [
            { fieldName: 'offerMechanic', value: mechanic },
            { fieldName: 'productOfferGroups', value: groups },
            { fieldName: 'products', value: [] },
            { fieldName: 'splitPromotion', value: false },
            { fieldName: 'startDate', value: null },
            { fieldName: 'endDate', value: null },
            { fieldName: 'promotionType', value: 'standard' },
            { fieldName: 'additionalClientSpecificFields', value: {} },
        ],
    });
};

const addProductOfferGroups = ({ numberOfProductOfferGroupsToCreate = 1 } = {}) => {
    const draft = getDraft();
    if (!draft) return;
    const groups = [...draft.productOfferGroups];
    const newGroups = [];
    const nextId = Math.max(0, ...groups.map(group => group._id)) + 1;
    for (let index = 0; index < numberOfProductOfferGroupsToCreate; index += 1) {
        const group = {
            _id: nextId + index,
            description: `Offer group ${groups.length + 1}`,
            products: [],
        };
        groups.push(group);
        newGroups.push(group);
    }

    const offerMechanic = cloneDeep(draft.offerMechanic);
    offerMechanic.tiers = (offerMechanic.tiers || []).map(tier => ({
        ...tier,
        productOfferGroups: [
            ...(tier.productOfferGroups || []),
            ...newGroups.map(group => ({
                _id: group._id,
                description: group.description,
                isOptionalOfferGroup: false,
                rewards: [],
                requirements: [],
            })),
        ],
    }));

    store.commit('promotions/setStagingAreaFields', {
        namespace,
        fieldsToUpdate: [
            { fieldName: 'productOfferGroups', value: groups },
            { fieldName: 'offerMechanic', value: offerMechanic },
        ],
    });
};

const deleteProductOfferGroup = ({ productOfferGroupId }) => {
    const draft = getDraft();
    if (!draft) return;
    const groups = draft.productOfferGroups.filter(group => group._id !== productOfferGroupId);

    const offerMechanic = cloneDeep(draft.offerMechanic);
    offerMechanic.tiers = (offerMechanic.tiers || []).map(tier => ({
        ...tier,
        productOfferGroups: (tier.productOfferGroups || []).filter(
            group => group._id !== productOfferGroupId
        ),
    }));

    store.commit('promotions/setStagingAreaFields', {
        namespace,
        fieldsToUpdate: [
            { fieldName: 'productOfferGroups', value: groups },
            { fieldName: 'offerMechanic', value: offerMechanic },
        ],
    });
};

const removeLastProductOfferGroup = () => {
    const groups = getDraft()?.productOfferGroups || [];
    if (groups.length <= MIN_OFFER_GROUPS) return;
    deleteProductOfferGroup({ productOfferGroupId: groups[groups.length - 1]._id });
};

const toBlueprint = offerMechanic => {
    const blueprint = cloneDeep(offerMechanic);
    (blueprint.tiers || []).forEach(tier => {
        (tier.productOfferGroups || []).forEach(group => {
            group._id = null;
            group.description = null;
        });
    });
    return blueprint;
};

initialise();

watch(
    () => [props.modelValue.priceTagId, props.priceTags.length],
    () => {
        if (props.modelValue.priceTagId) return;
        const hasDefaultTag = props.priceTags.some(
            tag => tag.id === offerMechanicTemplates.newPricePerItem
        );
        if (hasDefaultTag) selectPriceTag(offerMechanicTemplates.newPricePerItem);
    },
    { immediate: true }
);

watch(
    () => getDraft()?.offerMechanic,
    value => {
        if (value) {
            emit('update:modelValue', {
                ...props.modelValue,
                templateStructure: toBlueprint(value),
            });
        }
    },
    { deep: true, immediate: true }
);

// onUnmounted (not before) so offer-mechanic is torn down before its namespace disappears.
onUnmounted(() => {
    store.commit('promotions/removeStagingAreaNamespace', namespace);
});
</script>

<style scoped lang="scss">
.configure-from-mechanic-form {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;

    &__notice {
        font-size: 1.3rem;
    }

    &__editor {
        padding: 1.6rem 2rem;
        border: 1px solid #dfe3e8;
        border-radius: 0.8rem;
    }

    &__group-controls {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.2rem;
        gap: 1.2rem;
    }

    &__group-count {
        font-size: 1.3rem;
        font-weight: 600;
        color: #1f2933;
    }

    &__tag-layouts {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        margin-bottom: 1.2rem;
    }

    &__tag-layouts-label {
        font-size: 1.3rem;
        font-weight: 600;
    }

    &__tag-layouts-hint {
        font-size: 1.2rem;
        color: #637381;
    }
}
</style>
